import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSEO, getSiteUrl } from '../../utils/seo';

export default function HomePage() {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    
    updateSEO({
      title: '岡山市の介護認定調査なら おかやま介護認定調査株式会社 | 経験豊富な有資格者が丁寧に対応',
      description: '岡山市で介護認定調査をお探しなら、おかやま介護認定調査株式会社へ。介護支援専門員の有資格者が、一人ひとりの状況を丁寧に把握し、適切な介護サービスにつながる正確な調査を実施。24時間以内の迅速対応で、岡山市全域に対応しています。',
      keywords: '岡山市,介護認定調査,介護保険,要介護認定,介護支援専門員,ケアマネージャー,訪問調査,更新調査,変更調査',
      canonical: `${siteUrl}/`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'LocalBusiness',
            '@id': `${siteUrl}/#organization`,
            name: 'おかやま介護認定調査株式会社',
            description: '岡山市を中心に介護認定調査を専門に行う企業。経験豊富な有資格者が、一人ひとりの状況を丁寧に把握し、適切な介護サービスにつながる調査を実施。',
            url: siteUrl,
            telephone: '+81-86-012-3456',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '○○町1-2-3',
              addressLocality: '岡山市北区',
              addressRegion: '岡山県',
              postalCode: '700-0000',
              addressCountry: 'JP'
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 34.66167,
              longitude: 133.88235
            },
            openingHoursSpecification: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              opens: '09:00',
              closes: '18:00'
            },
            priceRange: '介護保険適用',
            areaServed: {
              '@type': 'City',
              name: '岡山市'
            }
          },
          {
            '@type': 'Organization',
            '@id': `${siteUrl}/#organization`,
            name: 'おかやま介護認定調査株式会社',
            url: siteUrl,
            foundingDate: '2026',
            founder: {
              '@type': 'Person',
              name: '岡山太郎'
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '○○町1-2-3',
              addressLocality: '岡山市北区',
              addressRegion: '岡山県',
              postalCode: '700-0000',
              addressCountry: 'JP'
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+81-86-012-3456',
              contactType: 'customer service',
              areaServed: 'JP',
              availableLanguage: 'Japanese'
            }
          },
          {
            '@type': 'WebPage',
            '@id': `${siteUrl}/#webpage`,
            url: siteUrl,
            name: '岡山市の介護認定調査なら おかやま介護認定調査株式会社',
            description: '岡山市で介護認定調査をお探しなら、おかやま介護認定調査株式会社へ。経験豊富な有資格者が丁寧に対応します。',
            isPartOf: {
              '@id': `${siteUrl}/#website`
            },
            about: {
              '@id': `${siteUrl}/#organization`
            },
            primaryImageOfPage: {
              '@type': 'ImageObject',
              url: 'https://public.readdy.ai/ai/img_res/205a711b9b384b3f5404bb1995cbc223.jpg'
            }
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '介護認定調査とは何ですか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '介護保険サービスを利用するために必要な要介護度を判定するための調査です。調査員がご自宅を訪問し、心身の状態や日常生活の様子を確認させていただきます。'
                }
              },
              {
                '@type': 'Question',
                name: '調査にはどのくらい時間がかかりますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '通常、1時間から1時間半程度のお時間をいただいております。ご本人様の状態により前後する場合がございます。'
                }
              },
              {
                '@type': 'Question',
                name: '調査当日に必要なものはありますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '介護保険被保険者証、お薬手帳、診察券などをご用意ください。また、日頃の様子をよくご存じのご家族の同席をお願いしております。'
                }
              },
              {
                '@type': 'Question',
                name: '調査費用はかかりますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '介護認定調査の費用は介護保険から支払われますので、ご本人様やご家族様のご負担はございません。'
                }
              },
              {
                '@type': 'Question',
                name: '土日や祝日の調査も可能ですか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '基本的には平日の営業時間内での調査となりますが、ご事情により土日祝日の調査が必要な場合はご相談ください。可能な限り対応させていただきます。'
                }
              },
              {
                '@type': 'Question',
                name: '岡山市以外でも対応していただけますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '岡山市を中心に、近隣地域も対応しております。対応可能エリアについては、お気軽にお問い合わせください。'
                }
              }
            ]
          }
        ]
      }
    });

    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (navbar) {
        if (window.scrollY > 100) {
          navbar.classList.add('bg-white', 'shadow-md');
          navbar.classList.remove('bg-transparent');
        } else {
          navbar.classList.remove('bg-white', 'shadow-md');
          navbar.classList.add('bg-transparent');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20care%20manager%20visiting%20elderly%20person%20at%20home%20in%20Okayama%20Japan%2C%20warm%20natural%20lighting%2C%20caring%20atmosphere%2C%20modern%20Japanese%20interior%2C%20soft%20focus%20background%20showing%20traditional%20Japanese%20room%20elements%2C%20photorealistic%20style%2C%20high%20quality%20professional%20photography%2C%20compassionate%20healthcare%20worker%20with%20clipboard%20talking%20to%20senior%20citizen&width=1920&height=1080&seq=hero001&orientation=landscape"
            alt="岡山市の介護認定調査サービス - 専門調査員が丁寧に対応"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-block px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full mb-6">
                <span className="text-sm font-medium text-[#2C3E50]">岡山市密着型サービス</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                岡山市の介護認定調査を<br />
                誠実に、丁寧に<br />
                サポートいたします
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
                介護を必要とされる方々の適切な認定のために、<br className="hidden sm:block" />
                経験豊富な調査員が心を込めて対応します
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="tel:086-012-3456"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#2C3E50] text-white rounded-lg font-medium hover:bg-[#1a252f] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill text-xl"></i>
                  <span className="text-lg">086-012-3456</span>
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#5D9B9B] text-white rounded-lg font-medium hover:bg-[#4a7d7d] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <span className="text-lg">お問い合わせフォーム</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-[#2C3E50] text-white rounded-full text-sm font-medium mb-4">
              NEWS
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50]">お知らせ</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                date: '2026.01.15',
                title: '新年のご挨拶と営業日のお知らせ',
                link: '#'
              },
              {
                date: '2026.01.10',
                title: '介護認定調査に関する無料相談会を開催いたします',
                link: '#'
              },
              {
                date: '2025.12.25',
                title: '年末年始の営業時間変更のお知らせ',
                link: '#'
              },
              {
                date: '2025.12.01',
                title: '新しい調査員が加わりました',
                link: '#'
              }
            ].map((news, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300 border-l-4 border-[#5D9B9B] cursor-pointer"
              >
                <p className="text-sm text-gray-500 mb-3">{news.date}</p>
                <h3 className="text-lg font-bold text-[#2C3E50] mb-4 line-clamp-2">
                  {news.title}
                </h3>
                <a href={news.link} className="text-[#5D9B9B] font-medium hover:underline inline-flex items-center gap-2 cursor-pointer">
                  詳しく見る
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] mb-6">
              私たちの想い
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              介護を必要とされる方々に寄り添い、<br className="hidden sm:block" />
              誠実で温かみのある調査を心がけています
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20scene%20of%20Japanese%20care%20manager%20having%20friendly%20conversation%20with%20elderly%20Japanese%20person%20and%20family%20at%20home%2C%20sitting%20together%20at%20table%2C%20natural%20daylight%2C%20caring%20and%20trustworthy%20atmosphere%2C%20genuine%20smiles%2C%20professional%20yet%20warm%20interaction%2C%20photorealistic%20style%20showing%20compassion%20and%20respect&width=800&height=600&seq=values001&orientation=landscape"
                alt="利用者様とご家族に寄り添う調査員"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-[#5D9B9B] rounded-full flex items-center justify-center mb-4">
                  <i className="ri-heart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4">
                  一人ひとりに寄り添う姿勢
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  介護認定調査は、その方の今後の生活を大きく左右する重要な手続きです。私たちは、一人ひとりの状況を丁寧に把握し、心を込めて調査にあたることを大切にしています。利用者様とご家族の不安に寄り添い、安心していただける対応を心がけています。
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="mb-8">
                <div className="w-12 h-12 bg-[#2C3E50] rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4">
                  誠実さと信頼
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  岡山市を中心に、地域の介護関係者の皆様から信頼をいただいています。経験豊富な有資格者が、正確で公平な調査を実施し、適切な介護サービスにつながるよう努めています。地域に根ざした活動を通じて、介護を必要とされる方々を支えています。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#5D9B9B] text-white rounded-lg font-medium hover:bg-[#4a7d7d] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <span>会社概要を見る</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/3d04fd9a5946ba2a3041c589cb629d09.jpeg"
                alt="信頼される専門チーム"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-[#5D9B9B] text-white rounded-full text-sm font-medium mb-4">
              FAQ
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50]">よくある質問</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                question: '介護認定調査とは何ですか？',
                answer: '介護保険サービスを利用するために必要な要介護度を判定するための調査です。調査員がご自宅を訪問し、心身の状態や日常生活の様子を確認させていただきます。'
              },
              {
                question: '調査にはどのくらい時間がかかりますか？',
                answer: '通常、1時間から1時間半程度のお時間をいただいております。ご本人様の状態により前後する場合がございます。'
              },
              {
                question: '調査当日に必要なものはありますか？',
                answer: '介護保険被保険者証、お薬手帳、診察券などをご用意ください。また、日頃の様子をよくご存じのご家族の同席をお願いしております。'
              },
              {
                question: '調査費用はかかりますか？',
                answer: '介護認定調査の費用は介護保険から支払われますので、ご本人様やご家族様のご負担はございません。'
              },
              {
                question: '土日や祝日の調査も可能ですか？',
                answer: '基本的には平日の営業時間内での調査となりますが、ご事情により土日祝日の調査が必要な場合はご相談ください。可能な限り対応させていただきます。'
              },
              {
                question: '岡山市以外でも対応していただけますか？',
                answer: '岡山市を中心に、近隣地域も対応しております。対応可能エリアについては、お気軽にお問い合わせください。'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-[#5D9B9B] rounded-full flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-xl">Q</span>
                </div>
                <h3 className="text-lg font-bold text-[#2C3E50] mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/4f7bc6bf74f5f1693a91d67b6805d9c1.jpeg"
            alt="背景画像"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-10">
            介護認定調査に関するご質問、ご依頼を承ります
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-[#2C3E50] rounded-full text-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <span>お問い合わせはこちら</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}
